import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import campaignApi from "../api/campaignApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import {
  PERFORMANCE_QUERY_KEYS,
  RELATION_TYPES,
} from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";
import useCampaignOptions from "../hooks/useCampaignOptions";

const Reviews360Page = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const { campaignOptions, isLoading: campaignsLoading } = useCampaignOptions();
  const [showGuide, setShowGuide] = React.useState(false);
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canRead360 = can(PERFORMANCE_PERMISSIONS.APPRAISAL_360_READ);
  const canManage360 = can(PERFORMANCE_PERMISSIONS.APPRAISAL_360_MANAGE);

  if (!canRead360) {
    return (
      <ModulePageShell
        title="360 Reviews"
        description="Submit and track 360 reviews with relationship context."
      >
        <EmptyState message="You do not have permission to view 360 reviews." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.campaignReviews],
    queryFn: () => campaignApi.list360Reviews({ page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: campaignApi.submit360Review,
    onSuccess: () => {
      toast({
        title: "360 review submitted",
        status: "success",
        duration: 2000,
      });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.campaignReviews],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to submit review",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      campaignId: "",
      revieweeId: "",
      reviewerId: "",
      relationType: "PEER",
      overallRating: 0,
      strengths: "",
      improvements: "",
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="360 Reviews"
      description="Submit and track 360 reviews with relationship context."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canManage360 ? (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) =>
              createMutation.mutate({ ...values, competencyRatings: [] }),
            )}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Submit 360 Review</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Campaign</FormLabel>
                  <Select
                    placeholder="Select campaign"
                    {...register("campaignId")}
                    isDisabled={campaignsLoading}
                  >
                    {campaignOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Reviewee ID</FormLabel>
                  <Select
                    placeholder="Select reviewee"
                    {...register("revieweeId")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Reviewer ID</FormLabel>
                  <Select
                    placeholder="Select reviewer"
                    {...register("reviewerId")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <HStack>
                <FormControl>
                  <FormLabel>Relation</FormLabel>
                  <Select {...register("relationType")}>
                    {RELATION_TYPES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Overall Rating</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    step="0.5"
                    {...register("overallRating", { valueAsNumber: true })}
                  />
                </FormControl>
              </HStack>
              <HStack>
                <FormControl>
                  <FormLabel>Strengths</FormLabel>
                  <Input {...register("strengths")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Improvements</FormLabel>
                  <Input {...register("improvements")} />
                </FormControl>
              </HStack>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Submit Review
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to 360 reviews, but cannot submit reviews." />
      )}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No 360 reviews available." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Campaign</Th>
                <Th>Reviewee</Th>
                <Th>Reviewer</Th>
                <Th>Relation</Th>
                <Th>Rating</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>{item.campaignId?.name || item.campaignId || "-"}</Td>
                  <Td>{item.revieweeId?.name || item.revieweeId || "-"}</Td>
                  <Td>{item.reviewerId?.name || item.reviewerId || "-"}</Td>
                  <Td>{item.relationType || "-"}</Td>
                  <Td>{item.overallRating ?? 0}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default Reviews360Page;
